/**
 * Number formatting utilities for Turkish locale
 * Turkish format: 28.075,50 (dot for thousands, comma for decimal)
 */

/**
 * Parse a Turkish formatted number string to a JavaScript number
 * "28.075" → 28075 (Turkish: twenty-eight thousand seventy-five)
 * "28.075,50" → 28075.50
 * "28,50" → 28.50
 * 
 * @param {string|number} value - The value to parse
 * @returns {number} - Parsed number or 0 if invalid
 */
export function parseTurkishNumber(value) {
  if (value === null || value === undefined || value === '') {
    return 0;
  }
  
  // If already a number, return it
  if (typeof value === 'number') {
    return isNaN(value) ? 0 : value;
  }
  
  // Convert to string
  let str = String(value).trim();
  
  // Remove currency symbols and spaces
  str = str.replace(/[₺TL\s]/gi, '');
  
  // Check if it's Turkish format (has dot as thousands separator)
  // Turkish: 28.075,50 or 28.075
  // English: 28,075.50 or 28.075
  
  const hasDot = str.includes('.');
  const hasComma = str.includes(',');
  
  if (hasDot && hasComma) {
    // Both separators present
    const dotPos = str.lastIndexOf('.');
    const commaPos = str.lastIndexOf(',');
    
    if (commaPos > dotPos) {
      // Turkish format: 28.075,50 (comma is decimal)
      str = str.replace(/\./g, '').replace(',', '.');
    } else {
      // English format: 28,075.50 (dot is decimal)
      str = str.replace(/,/g, '');
    }
  } else if (hasDot && !hasComma) {
    // Only dot - could be:
    // - Turkish thousands: 28.075 (28075)
    // - English decimal: 28.075 (28.075)
    
    // Count digits after last dot
    const parts = str.split('.');
    const afterDot = parts[parts.length - 1];
    
    // If exactly 3 digits after dot, it's likely Turkish thousands separator
    // If 1-2 digits after dot, it's likely a decimal
    if (afterDot.length === 3 && parts.length > 1) {
      // Turkish thousands separator - remove all dots
      str = str.replace(/\./g, '');
    }
    // Otherwise keep as is (English decimal)
  } else if (hasComma && !hasDot) {
    // Only comma - Turkish decimal: 28,50
    str = str.replace(',', '.');
  }
  
  const num = parseFloat(str);
  return isNaN(num) ? 0 : num;
}

/**
 * Format a number for display in Turkish locale
 * 28075.50 → "28.075,50"
 * 28075 → "28.075"
 * 
 * @param {number} value - The number to format
 * @param {object} options - Formatting options
 * @param {number} options.minimumFractionDigits - Minimum decimal places (default: 0)
 * @param {number} options.maximumFractionDigits - Maximum decimal places (default: 2)
 * @returns {string} - Formatted string
 */
export function formatTurkishNumber(value, options = {}) {
  const num = typeof value === 'number' ? value : parseTurkishNumber(value);
  
  const {
    minimumFractionDigits = 0,
    maximumFractionDigits = 2
  } = options;
  
  return num.toLocaleString('tr-TR', {
    minimumFractionDigits,
    maximumFractionDigits
  });
}

/**
 * Format currency in Turkish Lira
 * 28075.50 → "₺28.075,50"
 * 28075 → "₺28.075"
 * 
 * @param {number} value - The amount to format
 * @param {boolean} showCurrency - Whether to show ₺ symbol (default: true)
 * @returns {string} - Formatted currency string
 */
export function formatTurkishCurrency(value, showCurrency = true) {
  const num = typeof value === 'number' ? value : parseTurkishNumber(value);
  
  const formatted = num.toLocaleString('tr-TR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });
  
  return showCurrency ? `₺${formatted}` : formatted;
}

/**
 * Input handler for Turkish number fields
 * Allows only valid Turkish number input (digits, dots for thousands, comma for decimal)
 * 
 * @param {string} value - The input value
 * @returns {string} - Sanitized value
 */
export function sanitizeTurkishNumberInput(value) {
  // Allow digits, dots, and comma
  return value.replace(/[^\d.,]/g, '');
}

export default {
  parseTurkishNumber,
  formatTurkishNumber,
  formatTurkishCurrency,
  sanitizeTurkishNumberInput
};
